import { Link } from "react-router-dom";
import { PublicHeader, PublicFooter, ChapterBadge } from "@/components/PublicChrome";
import { ArrowLeft, ScrollText, ShieldCheck, Scale } from "lucide-react";

const ICONS = {
  "mentions-legales": ScrollText,
  cgv: Scale,
  confidentialite: ShieldCheck,
};

const SECTIONS = {
  "mentions-legales": {
    title: "Mentions légales",
    chapter: "Annexe I · Le registre",
    subtitle: "Les mentions obligatoires du site.",
    body: `
**Éditeur du site**
Picnic-Game — SAS au capital de 1000€
Siège social : Figeac, France
RCS : à compléter
Directeur de publication : Admin Picnic-Game

**Hébergement**
Plateforme Emergent — emergent.sh

**Propriété intellectuelle**
L'ensemble du contenu de ce site (textes, images, photos, logos) est protégé par le droit d'auteur. Toute reproduction non autorisée est interdite.

**Contact**
contact@picnicgame.fr
`,
  },
  cgv: {
    title: "Conditions Générales de Vente",
    chapter: "Annexe II · Le contrat",
    subtitle:
      "Les règles qui encadrent chaque réservation et chaque partie jouée.",
    body: `
**Article 1 — Objet**
Les présentes CGV régissent les ventes de parcours d'escape-game outdoor "Picnic-Game".

**Article 2 — Réservation**
La réservation s'effectue en ligne sur picnicgame.fr. Le paiement est dû au moment de la réservation. Une confirmation est envoyée par email.

**Article 3 — Annulation**
Annulation gratuite jusqu'à 48h avant le jeu. En deçà, la réservation est non remboursable.

**Article 4 — Météo**
En cas d'intempéries majeures, le jeu peut être reporté gratuitement.

**Article 5 — Litiges**
Tribunal compétent : Cahors.
`,
  },
  confidentialite: {
    title: "Politique de confidentialité",
    chapter: "Annexe III · Le secret",
    subtitle:
      "Ce que nous gardons, ce que nous protégeons, ce que vous pouvez demander.",
    body: `
**Données collectées**
Nous collectons uniquement les données nécessaires à la réservation : prénom, email, téléphone (optionnel), date et créneau de jeu.

**Finalité**
Ces données sont utilisées pour confirmer la réservation, gérer le jeu et envoyer les codes d'accès.

**Conservation**
Les données sont conservées 3 ans après la dernière réservation.

**Vos droits**
Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression. Contactez-nous à contact@picnicgame.fr.

**Cookies**
Ce site utilise uniquement des cookies techniques (session) nécessaires au fonctionnement. Aucun cookie publicitaire ni traceur tiers.
`,
  },
};

function renderBody(text) {
  // Convert **Titre** → h3, other lines → paragraph
  const blocks = text.trim().split(/\n\s*\n/);
  return blocks.map((block, i) => {
    const m = block.match(/^\*\*(.+?)\*\*\s*\n?([\s\S]*)$/);
    if (m) {
      return (
        <div key={i} className="mb-8">
          <h3 className="font-display text-lg md:text-xl text-or mb-3 tracking-wide">
            {m[1]}
          </h3>
          <p className="text-parchemin/80 leading-relaxed whitespace-pre-line">
            {m[2].trim()}
          </p>
        </div>
      );
    }
    return (
      <p
        key={i}
        className="text-parchemin/80 leading-relaxed mb-6 whitespace-pre-line"
      >
        {block}
      </p>
    );
  });
}

export default function Legal({ section }) {
  const c = SECTIONS[section];
  if (!c) {
    return (
      <div
        className="min-h-screen bg-noir text-parchemin flex flex-col"
        data-testid="legal-notfound"
      >
        <PublicHeader />
        <main className="flex-1 flex items-center justify-center p-8 text-center">
          <div>
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-or/70">
              404
            </div>
            <p className="mt-4 font-display text-2xl italic text-parchemin">
              Cette page n&apos;existe pas dans le registre.
            </p>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  const Icon = ICONS[section] || ScrollText;

  return (
    <div
      className="min-h-screen bg-noir text-parchemin bg-grain flex flex-col"
      data-testid={`legal-${section}`}
    >
      <PublicHeader />

      <main className="flex-1 px-6 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-parchemin/50 hover:text-or text-sm transition-colors"
            data-testid="legal-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Accueil
          </Link>

          <div className="mt-8 mb-10">
            <ChapterBadge testId="legal-chapter">{c.chapter}</ChapterBadge>
            <div className="mt-5 flex items-start gap-4">
              <div className="w-12 h-12 rounded-sm bg-or/10 border border-or/30 flex items-center justify-center flex-shrink-0">
                <Icon className="w-5 h-5 text-or" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="font-display font-medium text-3xl md:text-4xl text-parchemin leading-tight tracking-tight">
                  {c.title}
                </h1>
                <p className="mt-3 text-parchemin/60 text-base">{c.subtitle}</p>
              </div>
            </div>
          </div>

          <div className="border-t border-or/15 pt-10 space-y-2">
            {renderBody(c.body)}
          </div>

          <div className="mt-16 pt-8 border-t border-or/15 divider-ornament font-display italic text-or/60 text-xs">
            <span aria-hidden="true">✦</span>
            <span>Fin du registre</span>
            <span aria-hidden="true">✦</span>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
